import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App.tsx";
import "./styles/globals.css";
import { Provider } from "react-redux";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { ScErrorBoundary, scCreatePostHogReporter } from "@streamoid/ui";

import { store } from "./configureStore.js";
import { bootstrapAnalytics } from "./utils/analytics";

void bootstrapAnalytics();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 15_000,
    },
  },
});

/* A render crash produces no request: no span, no access-log line, no status
 * code. Every server-side signal is blind to it and the user gets a blank page,
 * so the browser is the only place it can be observed.
 *
 * The client is resolved through the lazy bootstrap rather than held here, so
 * reporting inherits its host allowlist and an environment where analytics is
 * disabled reports nothing instead of throwing.
 */
const reportRenderError = (
  report: Parameters<ReturnType<typeof scCreatePostHogReporter>>[0],
) => {
  void bootstrapAnalytics().then((posthog) => {
    scCreatePostHogReporter(posthog, { service: "unified-admin-dashboard" })(report);
  });
};

ReactDOM.createRoot(document.getElementById("root")!).render(
  <ScErrorBoundary service="unified-admin-dashboard" reporter={reportRenderError}>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </Provider>
  </ScErrorBoundary>
);
